import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { shellHost } from '@/components/shellHost';
import { useDialogInteraction } from '@/components/useDialogInteraction';

interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** A class beside `modal` on the dialog, for its size. */
  className?: string;
}

export function Modal({ title, onClose, children, className }: ModalProps) {
  const titleId = useId();
  const dialogRef = useDialogInteraction<HTMLDivElement>(true, onClose);
  // A click on the backdrop closes the dialog only when the PRESS began on the
  // backdrop too. A press that starts inside the dialog and ends outside it (a
  // drag, or a touch the browser re-targets — iOS closed the inspector from a
  // tap on a fretboard cell, user-found Sep 2026) is not a request to close.
  const pressedOnOverlay = useRef(true);

  // Into the app shell wherever the dialog is opened from: the inspector's
  // provider sits outside the shell, so inline it lost the style (shellHost).
  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onPointerDown={(e) => {
        pressedOnOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (!pressedOnOverlay.current) {
          pressedOnOverlay.current = true;
          return;
        }
        onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={className ? `modal ${className}` : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3 id={titleId}>{title}</h3>
          <button
            type="button"
            className="close-x close-circle"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    shellHost(),
  );
}
